import { useEffect, useState } from 'react';
import { drawRasterToCanvas, loadImageRaster } from '@/shared/lib';
import { type UseFacePreviewInput, useFacePreview } from './use-face-preview';

/** 상호작용이 멈춘 뒤 곡면 렌더를 시작하기까지 기다리는 시간(ms) — 연속 편집 중에는 다시 그리지 않는다. */
export const WARPED_PREVIEW_DELAY_MS = 150;

/** 바로 편집용 곡면 합성의 사진 픽셀 상한 — 화면 표시에 충분하고 메인 스레드 렌더를 짧게 둔다(최종 인쇄는 서버 렌더). */
export const WARPED_MAX_PIXELS = 900_000;

const loadPreviewRaster = (url: string) => loadImageRaster(url, { maxPixels: WARPED_MAX_PIXELS });

export interface WarpedPreview {
  /** 지금 문서 revision 으로 그린 곡면 합성(사진 전체 크기). 낡았거나 아직 없으면 null. */
  readonly bitmap: ImageBitmap | null;
  /** 마지막 렌더(사진·에셋 로드 포함) 시간 ms. */
  readonly renderMs: number | null;
}

type WarpedPreviewInput = Omit<UseFacePreviewInput, 'mode' | 'onSettled' | 'onFailed'> & {
  /** 곡면(원통) 배치가 있는 면에서만 켠다. 평면은 평면 편집 표면이 그대로 결과다. */
  readonly enabled: boolean;
};

const toBitmap = async (raster: Parameters<typeof drawRasterToCanvas>[1]): Promise<ImageBitmap> => {
  const canvas = document.createElement('canvas');
  drawRasterToCanvas(canvas, raster);
  return createImageBitmap(canvas);
};

/**
 * 곡면 상품의 바로 편집용 미리보기 — 문서가 바뀌고 WARPED_PREVIEW_DELAY_MS 동안 더 바뀌지 않으면 기존 면 미리보기
 * 파이프라인(useFacePreview, 사진·마스크·음영·워프)을 한 번 돌리고, 결과를 ImageBitmap 으로 바꿔 돌려준다.
 * 편집 모드를 바꾸지 않으므로 입력을 막지 않는다. 래스터 대신 비트맵만 화면에 넘긴다(개발 모드 props 기록 비용을 피한다).
 */
export const useWarpedPreview = ({ enabled, ...input }: WarpedPreviewInput): WarpedPreview => {
  const [target, setTarget] = useState<number | null>(null);
  const [rendered, setRendered] = useState<{ revision: number; bitmap: ImageBitmap } | null>(null);
  const [failedRevision, setFailedRevision] = useState<number | null>(null);
  const key = `${input.revision}:${input.faceKey}:${input.colorOptionId}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: 문서·면·색상 키가 바뀔 때만 다시 예약한다.
  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(() => setTarget(input.revision), WARPED_PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled, key]);

  const wanted =
    enabled &&
    target === input.revision &&
    rendered?.revision !== input.revision &&
    failedRevision !== input.revision;
  const preview = useFacePreview({
    ...input,
    mode: wanted ? 'rendering' : 'flat',
    deps: { loadRaster: loadPreviewRaster },
    onSettled: () => undefined,
    onFailed: (revision) => setFailedRevision(revision),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: 새 렌더 결과가 올 때만 비트맵으로 바꾼다.
  useEffect(() => {
    const raster = preview.result?.raster;
    if (raster === undefined || !wanted) return;
    const revision = input.revision;
    let cancelled = false;
    void toBitmap(raster).then((bitmap) => {
      if (cancelled) bitmap.close();
      else setRendered({ revision, bitmap });
    });
    return () => {
      cancelled = true;
    };
  }, [preview.result]);

  return {
    bitmap: enabled && rendered?.revision === input.revision ? rendered.bitmap : null,
    renderMs: preview.renderMs,
  };
};
